import React from 'react';
import Immutable from 'immutable';
import ParentBoundComponent from '../../components/parentboundcomponent';
import ResultSetModel from './resultsetmodel';
import DataTable from '../../components/data/datatable';
import FlexBox from '../../components/containers/flexbox';
import FlexBoxBlock from '../../components/containers/flexboxblock';
import styled from 'styled-components';
import {formatNumber} from '../../data/numberutils';
import { defaultTheme } from '../../data/themes';
import { RowCountMessage, Pager } from '../../components/data/pager';
import Icon from '../../components/icon';
import memoizeOne from 'memoize-one';
import StaticLookup from '../../data/staticlookup';
import LookupInput from '../../components/forms/lookupinput';
import { getPhrase } from '../../data/phrases';
import ModalContainer from '../../components/containers/modalcontainer';
import ColumnListEditor from './columnlisteditor';
import RootModalContainer from '../../components/containers/rootmodalcontainer';
import FilterEditor from './filtereditor';
import { isEmpty } from '../../data/validation';
import { showMessageBox } from '../../actions/modal';

const EMPTY_MAP = Immutable.Map({});
const EMPTY_LIST = Immutable.List([]);

export default class ResultSetModule extends ParentBoundComponent {
	constructor() {
		super();
		this.onColumnWidthChanged = this.onColumnWidthChanged.bind(this);
		this.setColumnWidths = this.setColumnWidths.bind(this);
		this.onRowSelected = this.onRowSelected.bind(this);
		this.onKeyDown = this.onKeyDown.bind(this);
		this.setFixedColumns = this.setFixedColumns.bind(this);
		this.onAvailableColumnMouseDown = this.onAvailableColumnMouseDown.bind(this);
		this.showColumnListEditor = this.showColumnListEditor.bind(this);
		this.hideColumnListEditor = this.hideColumnListEditor.bind(this);
		this.moveColumnBefore = this.moveColumnBefore.bind(this);
		this.removeColumn = this.removeColumn.bind(this);
		this.removeAllColumns = this.removeAllColumns.bind(this);
		this.onRowChecked = this.onRowChecked.bind(this);
		this.onAllRowsChecked = this.onAllRowsChecked.bind(this);
		this.onFilterMouseDown = this.onFilterMouseDown.bind(this);
		this.hideFilterEditor = this.hideFilterEditor.bind(this);
		this.showFilterEditor = this.showFilterEditor.bind(this);
		this.setFilterEditorFilter = this.setFilterEditorFilter.bind(this);
		this.deleteProfile = this.deleteProfile.bind(this);
		this.createProfile = this.createProfile.bind(this);
	}

	async deleteProfile() {
		const dlgRes = await showMessageBox(getPhrase('reallyDeleteProfile').replace('$name', this.props.selectedProfile), { type: 'confirm' });
		if (dlgRes !== 'yes') {
			return;
		}
		this.props.onDeleteProfileRequested(this.props.selectedProfile);
	}

	async createProfile() {
		let name = await showMessageBox(getPhrase('profileName'), { type: 'input' });
		name = (name || '').trim();
		if (!name) {
			return;
		}
		this.props.onCreateProfileRequested(name);
	}

	setFilterEditorFilter(value) {
		this.setModel(this.getModel().setFilterEditorFilter(value));
	}

	onRowChecked(rowIndex, value) {
		this.setModel(this.getModel().setRowChecked(rowIndex, value));
	}

	requestFocus() {
		if (this.props.onFocusRequested) {
			this.props.onFocusRequested();
		}
	}

	onAllRowsChecked(value) {
		this.setModel(this.getModel().setAllRowsChecked(value));
	}

	removeAllColumns() {
		this.setModel(this.getModel().removeAllColumns());
	}

	moveColumnBefore(col, before) {
		this.setModel(this.getModel().moveColumnBefore(col, before));
	}

	removeColumn(col) {
		this.setModel(this.getModel().removeColumn(col));
	}

	showColumnListEditor() {
		this.setModel(this.getModel().showColumnListEditor());
	}

	showFilterEditor() {
		this.setModel(this.getModel().showFilterEditor());
	}

	hideFilterEditor(saveChanges) {
		const filter = this.getData().getIn(['filterEditor', 'filter']);
		this.setModel(this.getModel().hideFilterEditor());
		if (saveChanges) {
			this.props.setFilter(filter);
		} else {
			this.requestFocus();
		}
	}

	hideColumnListEditor(saveChanges) {
		const output = this.getData().getIn(['columnListEditor', 'selectedColumns']);
		this.setModel(this.getModel().hideColumnListEditor());
		if (saveChanges && this.props.setOutput) {
			this.props.setOutput(output)
		}
	}

	onFilterMouseDown(e) {
		e.preventDefault();
		this.showFilterEditor();
	}

	onAvailableColumnMouseDown(e) {
		e.preventDefault();
		this.showColumnListEditor();
	}

	setFixedColumns(value) {
		this.setModel(this.getModel().setFixedColumns(value));
		if (this.props.onFixedColumnsChanged) {
			this.props.onFixedColumnsChanged(value);
		}
	}

	toggleRowChecked(rowIndex) {
		this.setModel(this.getModel().toggleRowChecked(rowIndex));
	}

	onKeyDown(e) {
		const keyCode = e.keyCode;
		let moveOffset = 0;
		if (keyCode === 38) {
			moveOffset = -1;
		} else if (keyCode === 40) {
			moveOffset = 1;
		} else if (keyCode === 13) {
			const row = this.getModel().getSelectedRow();
			if (row && this.props.onRowAction) {
				this.props.onRowAction(row, this.getData().get('selectedRowIndex') || 0, {
					ctrlKey: e.ctrlKey,
					altKey: e.altKey
				});
			}
		} else if (keyCode === 32) {
			this.toggleRowChecked(this.getModel().getSelectedRowIndex());
		} else if (keyCode === 114) {
			e.preventDefault();
			this.showFilterEditor();
		}

		if (moveOffset) {
			const model = this.getModel();
			this.onRowSelected(model.getSelectedRowIndex() + moveOffset);
		}
	}

	getModel() {
		return new ResultSetModel(this.getData());
	}

	setModel(model) {
		this.setData(model.getData());
	}

	onColumnWidthChanged(columnIndex, width) {
		this.setData(this.getModel().setColumnWidth(columnIndex, width).getData());
	}

	setColumnWidths(columnWidths) {
		this.setData(this.getModel().setColumnWidths(columnWidths).getData());
	}

	onRowSelected(rowIndex) {
		this.setData(this.getModel().setSelectedRowIndex(rowIndex).getData());
		if (this.props.onRowSelected) {
			this.props.onRowSelected(rowIndex);
		}
	}

	renderBottomPager(pager) {
		return <Pager
			pager={pager}
			spread={this.props.pagerSpread}
			prevButtonText={this.props.pagerPrevButtonText}
			nextButtonText={this.props.pagerNextButtonText}
			onPageRequested={this.props.onPageRequested}
		/>;
	}

	render() {
		const data = this.getData();
		const entityName = data.get('entityName');
		const pager = data.get('pager');
		const bottomPager = this.renderBottomPager(pager);
		const columns = data.get('columns');
		const fixedColumns= data.get('fixedColumns') || 0;
		const fixedColLookup = getFixedColLookup(columns);
		const availableColumns = data.get('availableColumns');
		const filterColumns = data.get('filterColumns');
		const columnListEditor = data.get('columnListEditor');
		const filterEditor = data.get('filterEditor');
		const rows = data.get('rows') || EMPTY_LIST;
		const selectedRowIndex = data.get('selectedRowIndex') || 0;
		const profileLookup = this.props.profileLookup;
		return (
			<FlexBox vertical>
				{
					(this.props.toolBar || pager || this.props.leftToolbar) && (
						<FlexBoxBlock
							textAlign="right"
							padding="0px 0px 8px 0px"
						>
							<FlexBox alignItems="center">
								<FlexBoxBlock>
									{
										fixedColLookup && (
											<FixedCols>
												<span className="label">{getPhrase('fixedColumns')}:</span>
												<LookupInput
													combo
													mandatory
													noDropDown
													getRows={fixedColLookup.getRows}
													getText={fixedColLookup.getText}
													value={fixedColumns}
													onChange={this.setFixedColumns}
													textAlign="center"
												/>
											</FixedCols>
										)
									}
									{
										(availableColumns || filterColumns) && (
											<ToolbarIcons>
												<Icon
													name="list"
													active
													disabled={!availableColumns}
													onMouseDown={this.onAvailableColumnMouseDown}
													hint={getPhrase('columnList')}
												/>
												<Icon
													name="filter"
													active
													disabled={!filterColumns || !this.props.setFilter}
													onMouseDown={this.onFilterMouseDown}
													hint={getPhrase('filter')}
												/>
												{
													getFilterText(data.get('filter'))
												}
											</ToolbarIcons>
										)
									}
									{
										profileLookup && (
											<ProfileSelection>
												<LookupInput
													combo
													mandatory
													getRows={profileLookup.getRows}
													getText={profileLookup.getText}
													value={this.props.selectedProfile}
													onChange={this.props.onSelectedProfileChanged}
												/>
												<Icon
													name="delete"
													disabled={!this.props.selectedProfile || !this.props.onDeleteProfileRequested}
													active
													hint={getPhrase('deleteProfile')}
													onMouseDown={this.deleteProfile}
												/>
												<Icon
													name="add"
													active
													disabled={!this.props.onCreateProfileRequested}
													hint={getPhrase('createProfile')}
													onMouseDown={this.createProfile}
												/>
											</ProfileSelection>
										)
									}
									{this.props.leftToolBar}
								</FlexBoxBlock>
								<FlexBoxBlock flexGrow="1"></FlexBoxBlock>
								<FlexBoxBlock>
									{
										this.props.toolBar
									}
									<RowCountMessage pager={pager} onPageRequested={this.props.onPageRequested}/>
								</FlexBoxBlock>
							</FlexBox>
						</FlexBoxBlock>
					)
				}
				<FlexBoxBlock flexGrow="1">
					<DataTable
						fixedColumns={fixedColumns}
						columns={columns}
						rows={rows}
						selectedRowIndex={selectedRowIndex}
						columnWidths={data.get('columnWidths')}
						onColumnWidthChanged={this.onColumnWidthChanged}
						setColumnWidths={this.setColumnWidths}
						leftIcons={this.props.leftIcons}
						onKeyDown={this.onKeyDown}
						getErrorMessage={data.get('getErrorMessage')}
						withRowCheckBox={data.get('withRowCheckBox')}
						onRowChecked={this.onRowChecked}
						onAllRowsChecked={this.onAllRowsChecked}
					/*	getColumnText={colName => {
							return getColumnText(entityName, colName, true);
						}}
						getColumnTitle={colName => {
							return getColumnText(entityName, colName);
						}}
						getErrorMessage={(colName, errorCode) => {
							return getErrorMessage(errorCode, entityName, colName);
						}}
						getIconHint={(iconId) => {
							return getIconHint(entityName, iconId);
						}}*/
						ctx={this.props.ctx}
						getRowClass={this.props.getRowClass}
						onRowAction={this.props.onRowAction}
						onIconMouseDown={this.props.onIconMouseDown}
						focusTimestamp={this.props.focusTimestamp}
						calculateColumnWidthTimestamp={data.get('calculateColumnWidthTimestamp')}
						onRowSelected={this.onRowSelected}
						onSortRequested={this.props.onSortRequested}
					/>
				</FlexBoxBlock>
				<FlexBoxBlock hidden={!bottomPager && !this.props.bottomRightToolbar}>
					<FlexBox alignItems="center">
						<FlexBoxBlock>{bottomPager}</FlexBoxBlock>
						<FlexBoxBlock flexGrow="1">{this.props.bottomMiddleToolbar}</FlexBoxBlock>
						<FlexBoxBlock>{this.props.bottomRightToolbar}</FlexBoxBlock>
					</FlexBox>
				</FlexBoxBlock>
				{
					columnListEditor && (
						<RootModalContainer noCenterer>
							<ColumnListEditor
								data={columnListEditor}
								moveColumnBefore={this.moveColumnBefore}
								removeColumn={this.removeColumn}
								removeAllColumns={this.removeAllColumns}
								onCloseRequested={this.hideColumnListEditor}
							/>
						</RootModalContainer>
					)
				}
				{
					filterEditor && (
						<RootModalContainer noCenterer>
							<FilterEditor
								data={filterEditor}
								onCloseRequested={this.hideFilterEditor}
								setFilterEditorFilter={this.setFilterEditorFilter}
							/>
						</RootModalContainer>
					)
				}
			</FlexBox>
		);

	}
}

const FixedCols = styled.span`
	display: inline-block;
	margin-right: 16px;

	> .label {
		display: inline-block;
		vertical-align: middle;
		font-weight: 700;
		opacity: 0.5;
		margin-right: 4px;
		font-size: 90%;
	}
	> .ps-input {
		vertical-align: middle;
		display: inline-block;
		width: 24px;
		padding: 0px;
		input {
			padding: 0px;
		}
	}
`;

const ProfileSelection = styled.span`
	display: inline-block;
	margin-right: 16px;
	> .lookup {
		display: inline-block;
		width: 180px;
	}
	> .icon {
		margin-left: 8px;
		font-size: 16px;
	}
`;

const ToolbarIcons = styled.span`
	display: inline-block;
	margin-right: 16px;
	vertical-align: middle;
	.icon {
		vertical-align: middle;
		margin-right: 8px;
	}
`;

const ZEROETH_COL_AS_LIST = Immutable.List([
	Immutable.Map({
		key: 0,
		cells: ['0', '-']
	})
]);

const getFixedColLookup = memoizeOne(cols => {
	if (!cols || cols.size === 0) {
		return null;
	}
	const rows = cols.slice(0, 9).map((col, index) => {
		return Immutable.Map({
			key: index + 1,
			cells: Immutable.List([index + 1, col.get('text') || col.get('name')] || ('#' + index))
		});
	});
	return StaticLookup(ZEROETH_COL_AS_LIST.concat(rows));
});

const FilterText = styled.div`
	font-weight: 700;
	display: inline-block;
	margin-left: -8px;
	color: ${props => props.theme.activeIconColor};
	cursor: default;
`;

function countFilterConditions(node) {
	if (!node) {
		return 0;
	}
	const nodes = node.get('nodes');
	let res = 0;
	if (nodes) {
		nodes.forEach(node => {
			res += countFilterConditions(node);
		})
	} else {
		const value = node.get('value');
		const operator = node.get('operator');
		if (!isEmpty(value) || operator === 'isNull' || operator === 'isNotNull' || operator === 'exists' || operator === 'notExists') {
			res = 1;
		}
	}
	return res;
}

const getFilterText = memoizeOne(filter => {
	const cnt = countFilterConditions(filter);
	if (cnt > 0) {
		return <FilterText title={getPhrase('resultSet.filterText').replace('$count', cnt)}>{cnt}</FilterText>
	}
	return null;
});
